import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Spinner,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { useGetImplementationDetailsQuery } from '../requests/tapeBuilder.gql.generated';
import { OrgSlugToOrgMapType } from './GraphContext';
import { TapeBuilderForPipelineDefinition } from './TapeBuilderForPipelineDefinition';

export const TapeBuilderForImplementation = ({
  implementationId,
  implementationRoutePrefix: _implementationRoutePrefix,
}: {
  implementationId: string;
  implementationRoutePrefix: string;
}) => {
  const { data, loading, error } = useGetImplementationDetailsQuery({
    variables: { implementationId },
  });

  const orgSlugMap = useMemo<OrgSlugToOrgMapType>(() => {
    if (!data?.getImplementationDetails) {
      return {};
    }

    const implementation = data.getImplementationDetails;
    const map: OrgSlugToOrgMapType = {};

    // Add owner organization (BaseOrganization type - minimal fields)
    // We'll set type to 'unknown' since BaseOrganization doesn't include it
    map[implementation.ownerOrganization.slug] = {
      id: implementation.ownerOrganization.id,
      name: implementation.ownerOrganization.name,
      slug: implementation.ownerOrganization.slug,
    };

    // Add borrower organization (Organization type - full fields)
    map[implementation.borrowerOrganization.slug] = {
      id: implementation.borrowerOrganization.id,
      name: implementation.borrowerOrganization.name,
      slug: implementation.borrowerOrganization.slug,
    };

    // Add all unique borrowers from credit facilities
    implementation.creditFacilities.forEach((cf) => {
      if (!map[cf.borrower.slug]) {
        map[cf.borrower.slug] = {
          id: cf.borrower.id,
          name: cf.borrower.name,
          slug: cf.borrower.slug,
        };
      }
    });

    return map;
  }, [data]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={8}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>
          Error loading implementation details: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  const implementation = data?.getImplementationDetails;

  if (!implementation) {
    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertDescription>
          Implementation not found for ID: {implementationId}
        </AlertDescription>
      </Alert>
    );
  }

  if (!implementation.borrowerPipelineDefinitionId) {
    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertDescription>
          This implementation does not have an associated borrower pipeline
          definition.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <TapeBuilderForPipelineDefinition
      pipelineDefinitionId={implementation.borrowerPipelineDefinitionId}
      orgSlugMap={orgSlugMap}
    />
  );
};
